/**
 * bpimbalance.ts — draw the BP channel's imbalance (card `t_28a039b9`). Compute nothing.
 *
 * Every number arrives from `/api/overlays/{a}/{tf}/bp_imbalance`, which is `ict.bp_imbalance`
 * (bp_method.md §5 row 16, lesson 022; the rules and their timecodes live in that module). This
 * file maps rows to shapes and decides no rule — `overlays.ts`' contract: geometry in, pixels out.
 *
 *   zone       a STAIRCASE per row: the full zone from the bar it became known on, then after
 *              each `step` the part still unfilled («неперекрытая часть все еще выступает для нас
 *              целью», 022 06:08), to the row's end (`filled` / `merged`) or the right edge while
 *              open. Nothing is drawn before the bar it was known on; a merged row whose last
 *              member's third candle already took part of it starts at that step (022 06:08).
 *   merged     a run of two or more gaps with untouched wicks is ONE imbalance (022 05:49): a
 *              marker `M×k` on its first candle, on the row that is not itself replaced.
 *   ended      a filled or merged-away row keeps its boxes (audit evidence) with no caption.
 *
 * OFF BY DEFAULT. NOT A SIGNAL: no entry, no stop, no size.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export type BpImbalanceStep = { idx: number; time: UTCTimestamp; lo: number; hi: number };

export type BpImbalanceRow = {
  is_bull: boolean;
  idx: number;
  time: UTCTimestamp;
  first_idx: number;
  knowable_idx: number;
  knowable_time: UTCTimestamp;
  members: [number, number, number][];
  lo: number;
  hi: number;
  steps: BpImbalanceStep[];
  remaining_lo: number;
  remaining_hi: number;
  end_idx: number | null;
  end_time: UTCTimestamp | null;
  end_reason: 'filled' | 'merged' | null;
};

export type BpImbalancePayload = {
  component: 'bp_imbalance';
  source: string;
  counts: { total: number };
  imbalances: BpImbalanceRow[];
};

const STYLE = {
  bull: { fill: 'rgba(0, 150, 136, 0.16)', border: 'rgba(0, 150, 136, 0.75)' },
  bear: { fill: 'rgba(156, 39, 176, 0.14)', border: 'rgba(156, 39, 176, 0.70)' },
  ended: { fill: 'rgba(120, 120, 120, 0.06)', border: 'rgba(120, 120, 120, 0.30)' },
};

/** A shape that began before the pane's first bar starts at that bar (the `sessions.ts` clamp). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export const imbalanceLabel = (r: BpImbalanceRow): string =>
  `BP imb${r.members.length > 1 ? ` ×${r.members.length}` : ''}${r.steps.length ? ' (partial)' : ''}`;

/** The staircase: one band per stretch of constant remaining part. A zero-height stretch (the
 *  fill bar) or a zero-width one (a step dated at the knowable bar) is not a zone and is not drawn. */
export function bpImbalanceBands(p: BpImbalancePayload, windowFirstTime: number | null = null): ZoneBand[] {
  const out: ZoneBand[] = [];
  for (const r of p.imbalances) {
    const open = r.end_reason == null;
    const style = open ? STYLE[r.is_bull ? 'bull' : 'bear'] : STYLE.ended;
    const stops: { from: UTCTimestamp; lo: number; hi: number }[] = [
      { from: r.knowable_time, lo: r.lo, hi: r.hi },
      ...r.steps.map((s) => ({ from: s.time, lo: s.lo, hi: s.hi })),
    ];
    stops.forEach((s, k) => {
      const to = k + 1 < stops.length ? stops[k + 1].from : r.end_time;
      if (!(s.hi > s.lo)) return;
      // A step AT the knowable bar (that bar took part of an earlier member, `ict.bp_imbalance`)
      // replaces the full zone at once: that first stretch has no width and is not a zone either.
      if (to != null && to === s.from) return;
      if (windowFirstTime != null && to != null && to < windowFirstTime) return;
      out.push({
        lo: s.lo,
        hi: s.hi,
        fromTime: clamp(s.from, windowFirstTime),
        toTime: to,
        fill: style.fill,
        border: style.border,
        // One caption per open row, on its current stretch; ended rows stay unlabelled.
        label: open && k === stops.length - 1 ? imbalanceLabel(r) : '',
        exactSpan: true,
      });
    });
  }
  return out;
}

/** `M×k` on the first candle of each merged run's final row; none when left of the pane. */
export function bpImbalanceMarkers(p: BpImbalancePayload, windowFirstTime: number | null = null): SeriesMarker<Time>[] {
  return p.imbalances
    .filter((r) => r.members.length > 1 && r.end_reason !== 'merged')
    .filter((r) => windowFirstTime == null || r.time >= windowFirstTime)
    .map((r) => ({
      time: r.time,
      position: r.is_bull ? ('belowBar' as const) : ('aboveBar' as const),
      color: STYLE[r.is_bull ? 'bull' : 'bear'].border,
      shape: 'square' as const,
      text: `M×${r.members.length}`,
    }))
    .sort((a, b) => (a.time as number) - (b.time as number));
}

export function bpImbalanceSummary(p: BpImbalancePayload): [string, string][] {
  const runs = p.imbalances.filter((r) => r.end_reason !== 'merged');
  const merged = runs.filter((r) => r.members.length > 1).length;
  const open = runs.filter((r) => r.end_reason == null);
  const partial = open.filter((r) => r.steps.length > 0).length;
  return [
    ['BP imbalance', `${runs.length} in window (${merged} merged runs, 022 05:49)`],
    ['open / of them partly filled', `${open.length} / ${partial} — the unfilled part stays a target (022 06:08)`],
    ['filled', String(runs.filter((r) => r.end_reason === 'filled').length)],
    ['what this is', 'closed bars only, untouched-wick merge — NOT an entry signal'],
  ];
}

/** ONE CALL for every shape and the rows describing them (`bpSessionsDrawing`'s shape). */
export function bpImbalanceDrawing(p: BpImbalancePayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  markers: SeriesMarker<Time>[];
  rows: [string, string][];
} {
  return {
    bands: bpImbalanceBands(p, windowFirstTime),
    markers: bpImbalanceMarkers(p, windowFirstTime),
    rows: bpImbalanceSummary(p),
  };
}
